import { CloseRounded } from "@mui/icons-material";
import { Dialog, DialogTitle, Box, Button, DialogActions, DialogContent, IconButton, TextField, FormControl, MenuItem, Select, InputLabel, SelectChangeEvent, Typography } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { deleteDoc, doc, getDoc, getFirestore, setDoc } from "firebase/firestore";
import { createUserWithEmailAndPassword, deleteUser, getAuth, updateProfile } from 'firebase/auth';
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Employee, EmployeeStatus } from "../../types/Employee";
import { queryChiefSupervisors, querySupervisors } from "../../utils/queryEmployees";
import { v4 as uuidv4 } from 'uuid';

const BUILDINGS = ['A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10', 'A11'];
const BUILDINGS_VAL = ['A1', 'A2', 'A3', 'A4', 'B1', 'B2', 'C', 'D1', 'D2', 'E', 'A11'];
const BUILDINGS_WITH_DOOR = ['A1', 'A2', 'A3', 'A4', 'A11'];

const ROOMS = Array.from({length: 70}, (_, i) => i + 1);

const OCCUPATIONS = ['LABOR', 'DRIVER', 'TAILOR', 'TEA BOY', 'OFFICE', 'CAR WASH', 'WIELDER',
'PAINTER', 'CONSTRUCTION', 'BIG EQUIPMENT DRIVER', 'ELECTRICIAN', 'MAKBARA', 'SECURITY', 
'AC TEACHNICIAN', 'DIESEL MECHANIC', 'PETROL MECHANIC', 'OIL', 'PUNCHER', 'SAMKARA', 'MAKRATA'];

interface EmployeeFormProps {
  open: boolean,
  employee?: Employee,
  type?: number,
  onClose?: () => void,
  onSave?: (employee: Employee) => void,
};
export const EmployeeForm = (props: EmployeeFormProps) => {
  const { open, employee, type, onClose, onSave } = props;
  const { enqueueSnackbar } = useSnackbar();

  const eType = employee ? employee.type : type ?? 2;

  const h = (employee !== undefined && employee.dateHired !== '')
    ? employee.dateHired
    : DateTime.now().toFormat('yyyyMMdd');
  const a = (employee?.lodge?.building !== undefined) ? employee.lodge.building : '';
  const b = (employee?.lodge?.room !== undefined) ? employee.lodge.room : '';
  const c = (employee?.lodge?.door !== undefined) ? employee.lodge.door : '';

  const s = employee?.supervisor;
  const f = employee?.chiefSupervisor;

  // const [department, setDepartment] = useState('AL NADAFA');
  const [hire, setHire] = useState(h);
  const [building, setBuilding] = useState(a);
  const [room, setRoom] = useState(b);
  const [door, setDoor] = useState(c);

  const [department, setDepartment] = useState(employee?.department ?? 'AL NADAFA');
  const [idNumber, setIdNumber] = useState(employee?.idNumber ?? '');
  const [supervisors, setSupervisors] = useState<Employee[]>([]);
  const [supervisorsFiltered, setSupervisorsFiltered] = useState<Employee[]>([]);
  const [chiefSupervisors, setChiefSupervisors] = useState<Employee[]>([]);
  const [supervisor, setSupervisor] = useState<Employee>();
  const [chiefSupervisor, setChiefSupervisor] = useState<Employee>();

  let occupations = [...OCCUPATIONS];
  if (eType !== 2) {
    if (eType === 0)
      occupations = ['CHIEF SUPERVISOR'];
    else if (eType === 1)
      occupations = ['SUPERVISOR'];
  }

  const title = employee 
    ? `Edit Employee "${employee.idNumber}"` 
    : eType === 0
    ? 'Add Chief Supervisor'
    : eType === 1
    ? 'Add Supervisor'
    : 'Add Employee';

  const formOnSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    
    //name iqamaNumber nationality passportNumber department occupation dateHired phoneNumber
    const idNumber = employee ? employee.idNumber : formData.get('idNumber')!.toString();
    const name = formData.get('name')!.toString();
    const iqamaNumber = formData.get('iqamaNumber')!.toString();
    const nationality = formData.get('nationality')!.toString();
    const passportNumber = department !== 'AL NADAFA' ? '' : formData.get('passportNumber')!.toString();
    const occupation = department !== 'AL NADAFA' ? '' : formData.get('occupation')!.toString();
    const dateHired = hire // formData.get('dateHired')!.toString();
    const phoneNumber = formData.get('phoneNumber')!.toString();
    const building = department !== 'AL NADAFA' ? '' : formData.get('building');
    const room = department !== 'AL NADAFA' ? '' : formData.get('room');
    const door = department !== 'AL NADAFA' ? '' : formData.get('door');
    const supervisor = department !== 'AL NADAFA' ? '' : formData.get('supervisor');
    const chiefSupervisor = department !== 'AL NADAFA' ? '' : formData.get('chiefSupervisor');

    const data = {
      idNumber,
      name,
      iqamaNumber,
      nationality,
      passportNumber,
      department,
      occupation,
      dateHired, // : DateTime.fromFormat(dateHired, 'yyyy/MM/dd').toFormat('yyyyMMdd'),
      phoneNumber,
      lodge: {
        building: building ? building.toString() : '',
        room: room ? Number(room.toString()) : 0,
        door: door ? door.toString() : '',
      },
      supervisor: supervisor ? supervisor.toString() : '',
      chiefSupervisor: chiefSupervisor ? chiefSupervisor.toString() : '',
      taggedForDeletion: false,
      status: 'Active' as EmployeeStatus,
    };

    const save = async () => {
      const db = getFirestore();
      if (employee) {
        // We are in edit mode
        return await setDoc(doc(db, `Employees/${employee.idNumber}`), data, { merge: true }).then(() => {
          const d = { ...employee, ...data } as Employee;
          return d;
        });
      } else {
        const e: Employee = {
          ...data,
          uid: '',
          type: eType,
          salary: '',
        };

        try {
          if (idNumber.trim() === '')
            throw 'Please enter employee Id Number';

          // Check if employee with id number alread exists;
          const theEmployee = await getDoc(doc(db, `Employees/${e.idNumber}`));
          if (theEmployee.exists()) {
            throw `Employee with id number '${idNumber}' already exists!`;
          }

          await setDoc(doc(db, `Employees/${e.idNumber}`), e, { merge: true });
          // Create only a user when department is AL NADAFA
          if (department === 'AL NADAFA') {
            try {
              const auth = getAuth();
              const userCred = await createUserWithEmailAndPassword(auth, `u_${idNumber}@nadafa-ems.com`, `A${idNumber}a`);
              
              // Update employee uid
              await setDoc(doc(db, `Employees/${e.idNumber}`), { uid: userCred.user.uid }, { merge: true });
  
              // Update user profile
              await updateProfile(userCred.user, { displayName: name });
  
              return { ...e, uid: userCred.user.uid } as Employee;
            } catch (reason: any) {
              await deleteDoc(doc(db, `Employees/${e.idNumber}`));
              // enqueueSnackbar(reason, { variant: 'error' });
              // console.log(reason);
              throw reason;
            }
          } else {
            return e;
          }
        } catch (reason: any) {
          // enqueueSnackbar(reason, { variant: 'error' });
          //  console.log(reason);
          throw reason;
        }
      }
    };

    save().then((e) => {
      if (e !== undefined) {
        enqueueSnackbar(`Employee '${e.idNumber}' has been successfully saved!`, { variant: 'success' });
        onSave && onSave(e);
      }
    }).catch(reason => {
      if (typeof(reason) === 'string') {
        enqueueSnackbar(reason, { variant: 'error' });
      } else if (typeof(reason) === 'object') {
        enqueueSnackbar(reason.message, { variant: 'error' });
      } else {
        enqueueSnackbar('An error has occured while saving the information!', { variant: 'error' });
      }
    });
  };

  useEffect(() => {
    setIdNumber(employee?.idNumber ?? '');
    setHire(h);
    setBuilding(a);
    setRoom(b);
    setDoor(c);
    if (s) setSupervisor(supervisors.find((v) => v.idNumber === s));
    else setSupervisor(undefined);
    if (f) setChiefSupervisor(chiefSupervisors.find((v) => v.idNumber === f));
    else setChiefSupervisor(undefined);
  }, [employee]);

  useEffect(() => {
    setDepartment(employee?.department ?? 'AL NADAFA');
  }, [open]);

  useEffect(() => {
    querySupervisors().then((s) => setSupervisors(s));
    queryChiefSupervisors().then((s) => setChiefSupervisors(s));
  }, [])

  return (
    <Dialog open={open} onClose={onClose} sx={{ backdropFilter: 'blur(5px)' }} fullWidth maxWidth='md'>
      <Box
        component="form"
        sx={{
          '& .MuiTextField-root, & .MuiFormControl-root': { my: 1.5 },
        }}
        method="POST"
        onSubmit={formOnSubmit} 
        noValidate
        autoComplete="off"
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {title}
          {onClose ? (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseRounded />
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <TextField name="idNumber" value={idNumber} onChange={(e) => setIdNumber(e.currentTarget.value)} label="Id Number" sx={{ flexGrow: 1, marginInlineEnd: 1.5 }} InputProps={{ readOnly: Boolean(employee) }} />
              <TextField name="iqamaNumber" label="Iqama Number" defaultValue={employee?.iqamaNumber} sx={{ flexGrow: 1, marginInlineStart: 1.5 }} />
            </Box>
            <TextField name="name" label="Name" defaultValue={employee?.name} fullWidth />
            {(department === 'AL NADAFA' || department === 'WORKSHOPS ') ? (
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <TextField name="nationality" label="Nationality" defaultValue={employee?.nationality} sx={{ flexGrow: 1, marginInlineEnd: 1.5 }} />
                <TextField name="passportNumber" label="Passport Number" defaultValue={employee?.passportNumber} sx={{ flexGrow: 1, marginInlineStart: 1.5 }} />
              </Box>
            ) : (
              <TextField name="nationality" label="Nationality" defaultValue={employee?.nationality} fullWidth />
            )}
            {(department === 'AL NADAFA' || department === 'WORKSHOPS ') ? (
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                {/* <TextField 
                  name="department" 
                  label="Department" 
                  defaultValue="AL NADAFA" 
                  fullWidth
                  sx={{ flexGrow: 1, marginInlineEnd: 3 }}
                  // InputProps={{ readOnly: true }} 
                /> */}
                <FormControl sx={{ flexGrow: 1, marginInlineEnd: 1.5 }} fullWidth>
                  <InputLabel id="employee-department-select">Department</InputLabel>
                  <Select
                      labelId="employee-department-select"
                      label="Department"
                      value={department}
                      name="department"
                      onChange={(event: SelectChangeEvent<string>) => {
                        setDepartment(event.target.value);
                      }}
                    >
                      <MenuItem value='AL NADAFA'>AL NADAFA</MenuItem>
                      <MenuItem value='WORKSHOPS '>WORKSHOPS</MenuItem>
                      <MenuItem value='ZARAI'>ZARAI</MenuItem>
                      <MenuItem value='FINNI'>FINNI</MenuItem>
                      <MenuItem value='SAYANA'>SAYANA</MenuItem>
                      <MenuItem value='AL TORK'>AL TORK</MenuItem>
                      <MenuItem value='SALAMA'>SALAMA</MenuItem>
                      <MenuItem value='MAIN OFFICE'>MAIN OFFICE (TEA BOYS)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ flexGrow: 1, marginInlineStart: 1.5 }} fullWidth>
                  <InputLabel id="employee-occupation-select">Occupation</InputLabel>
                  <Select
                    labelId="employee-occupation-select"
                    label="Occupation"
                    defaultValue={employee?.occupation ?? 'LABOR'}
                    name="occupation"
                  >
                    {occupations.map((b) => (
                      <MenuItem key={b} value={b}>{b}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <FormControl fullWidth>
                <InputLabel id="employee-department-select">Department</InputLabel>
                <Select
                    labelId="employee-department-select"
                    label="Department"
                    value={department}
                    name="department"
                    onChange={(event: SelectChangeEvent<string>) => {
                      setDepartment(event.target.value);
                    }}
                  >
                    <MenuItem value='AL NADAFA'>AL NADAFA</MenuItem>
                    <MenuItem value='WORKSHOPS '>WORKSHOPS</MenuItem>
                    <MenuItem value='ZARAI'>ZARAI</MenuItem>
                    <MenuItem value='FINNI'>FINNI</MenuItem>
                    <MenuItem value='SAYANA'>SAYANA</MenuItem>
                    <MenuItem value='AL TORK'>AL TORK</MenuItem>
                    <MenuItem value='SALAMA'>SALAMA</MenuItem>
                    <MenuItem value='MAIN OFFICE'>MAIN OFFICE (TEA BOYS)</MenuItem>
                </Select>
              </FormControl>
            )}
            {(department === 'AL NADAFA' || department === 'WORKSHOPS ') ? (
              <LocalizationProvider dateAdapter={AdapterLuxon} >
                <DatePicker
                  label="Date Hired"
                  format="MMMM dd, yyyy"
                  value={DateTime.fromFormat(hire, 'yyyyMMdd')}
                  slotProps={{ textField: { fullWidth: true } }}
                  onChange={(selectedDate) => {
                    if (selectedDate !== null) {
                      setHire(selectedDate.toFormat('yyyyMMdd'));
                    }
                  }}
                />
              </LocalizationProvider>
            ) : null}
            <TextField name="phoneNumber" label="Phone Number" defaultValue={employee?.phoneNumber} fullWidth />
            {(department === 'AL NADAFA' || department === 'WORKSHOPS ') ? (
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <FormControl sx={{ flexGrow: 1, marginInlineEnd: 1.5 }}>
                  <InputLabel id="employee-building-select">Building</InputLabel>
                  <Select
                      labelId="employee-building-select"
                      label="Building"
                      defaultValue={building}
                      value={building}
                      name="building"
                      onChange={(event: SelectChangeEvent<string>) => {
                        if (!BUILDINGS_WITH_DOOR.includes(event.target.value))
                          setDoor('');
                        setBuilding(event.target.value);
                      }}
                    >
                      {BUILDINGS.map((b, i) => (
                        <MenuItem key={b} value={BUILDINGS_VAL[i]}>{b}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flexGrow: 1, marginInlineStart: 1.5, marginInlineEnd: 1.5 }}>
                  <InputLabel id="employee-room-select">Room</InputLabel>
                  <Select
                      labelId="employee-room-select"
                      label="Room"
                      defaultValue={room}
                      value={room}
                      name="room"
                      onChange={(event: SelectChangeEvent<number | string>) => setRoom(event.target.value as number)}
                    >
                      {ROOMS.map((b) => (
                        <MenuItem key={b} value={b}>{b}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flexGrow: 1, marginInlineStart: 1.5 }} disabled={!building || !BUILDINGS_WITH_DOOR.includes(building) || room === 0}>
                  <InputLabel id="employee-door-select">Door</InputLabel>
                  <Select
                      labelId="employee-door-select"
                      label="Door"
                      defaultValue={door}
                      value={door}
                      name="door"
                      onChange={(event: SelectChangeEvent<string>) => setDoor(event.target.value)}
                    >
                      {['A', 'B'].map((b) => (
                        <MenuItem key={b} value={b}>{b}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            ) : null}
            {eType !== 0 && (department === 'AL NADAFA' || department === 'WORKSHOPS ') && (
              <FormControl fullWidth>
                <InputLabel id="employee-chief-select">Chief Supervisor</InputLabel>
                <Select
                    labelId="employee-chief-select"
                    label="Chief Supervisor"
                    name="chiefSupervisor"
                    fullWidth
                    value={chiefSupervisor?.idNumber ?? ''}
                    onChange={(event) => {
                      const e = chiefSupervisors.find((s) => s.idNumber === event.target.value);
                      if (e) {
                        setChiefSupervisor(e);
                        setSupervisorsFiltered(supervisors.filter((s) => s.chiefSupervisor === e.idNumber));
                      }
                    }}
                  >
                    {chiefSupervisors.map((b) => (
                      <MenuItem key={b.idNumber} value={b.idNumber}>
                        <Typography
                          sx={{ display: 'inline', fontWeight: 700 }}
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >{b.idNumber}</Typography>
                        &nbsp; <Typography color="#808080" sx={{ display: 'inline' }}>{`— ${b.name}`}</Typography>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            {eType === 2 && (department === 'AL NADAFA' || department === 'WORKSHOPS ') && (
              <FormControl fullWidth>
                <InputLabel id="employee-supervisor-select">Supervisor</InputLabel>
                <Select
                    labelId="employee-supervisor-select"
                    label="Supervisor"
                    name="supervisor"
                    value={supervisor?.idNumber ?? ''}
                    fullWidth
                    onChange={(event: SelectChangeEvent<string>) => {
                      const e = supervisorsFiltered.find((s) => s.idNumber === event.target.value);
                      if (e)
                        setSupervisor(e);
                    }}
                  >
                    {supervisorsFiltered.map((b) => (
                      <MenuItem key={b.idNumber} value={b.idNumber}>
                        <Typography
                          sx={{ display: 'inline', fontWeight: 700 }}
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >{b.idNumber}</Typography>
                        &nbsp; <Typography color="#808080" sx={{ display: 'inline' }}>{`— ${b.name}`}</Typography>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
        </DialogContent>
        <DialogActions>
          <Button type="submit" autoFocus disabled={idNumber.trim() === ''}>
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};